import React, { useState, useEffect } from "react";
import "../styles/Admin.css";
import { FaPencilAlt, FaEye } from "react-icons/fa";
import { ref as databaseRef, onValue, update, get } from "firebase/database";
import { db } from "../../firebaseConfig";
import { useNavigate } from "react-router-dom";

import {
  Modal,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";


const ListaImoveis = () => {
  const [imoveis, setImoveis] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [corretores, setCorretores] = useState({});

  const [updatingStatus, setUpdatingStatus] = useState({});

  const navigate = useNavigate();

  const [visibleCount, setVisibleCount] = useState(18);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 18);
  };


  const handleNavigate = (id) => {
    navigate(`/panel/EditImovel/${id}`);
  };

  useEffect(() => {
    const imoveisRef = databaseRef(db, "imoveis/");
    onValue(imoveisRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const normalized = Object.keys(data)
          .map((key) => ({ id: key, ...data[key] }))
          .filter(Boolean);

        // Alguns registros em /imoveis não têm imovelInfo (ex.: sobra de um
        // gatilho de watermark que escreveu de volta em control/ depois do
        // imóvel já ter sido removido) — sem imovelInfo.codigo não há como
        // ordená-los junto dos demais. Em vez de deixar isso derrubar a
        // página inteira, separamos: registros com código utilizável são
        // ordenados normalmente; os incompletos vão para o fim da lista,
        // ainda visíveis (com fallback na renderização), para que o admin
        // consiga localizá-los e decidir o que fazer.
        const valid = [];
        const incomplete = [];

        normalized.forEach((imovel) => {
          const codigo = imovel?.imovelInfo?.codigo;
          const hasUsableCodigo =
            codigo !== undefined && codigo !== null && codigo !== "";

          if (hasUsableCodigo) {
            valid.push(imovel);
          } else {
            if (process.env.NODE_ENV !== "production") {
              console.warn(
                `[ListaImoveis] imóvel "${imovel.id}" sem imovelInfo.codigo utilizável (schema incompleto) — listado no fim, fora da ordenação por código.`,
                imovel
              );
            }
            incomplete.push(imovel);
          }
        });

        valid.sort((a, b) => {
          const codigoA = parseInt(a.imovelInfo.codigo, 10);
          const codigoB = parseInt(b.imovelInfo.codigo, 10);
          const safeA = Number.isFinite(codigoA) ? codigoA : 0;
          const safeB = Number.isFinite(codigoB) ? codigoB : 0;
          return safeB - safeA; // Ordenação decrescente (preservada)
        });

        setImoveis([...valid, ...incomplete]);
      } else {
        setImoveis([]);
      }
    });

    const corretoresRef = databaseRef(db, "corretores/");
    onValue(corretoresRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCorretores(data);
      }
    });
  }, []);

  const filteredImoveis = imoveis.filter((imovel) => {
    const codigo = imovel?.imovelInfo?.codigo || "";
    const bairro = imovel?.imovelInfo?.bairro || "";
    const cidade = imovel?.imovelInfo?.cidade || "";
    const owner = imovel?.ownerInfo?.nome || "";

    const searchLower = searchTerm.toLowerCase();
    return (
      codigo.toString().includes(searchLower) ||
      bairro.toLowerCase().includes(searchLower) ||
      cidade.toLowerCase().includes(searchLower) ||
      owner.toLowerCase().includes(searchLower)
    );
  });

  // Espelha imovelInfo/status em /imoveisPublicos junto com a escrita em /imoveis,
  // num único multi-path update (atômico). Só inclui o caminho público se o nó já
  // existir — senão um update() em filho de nó inexistente cria um registro
  // público incompleto (mesma armadilha corrigida antes no watermark).
  const syncPublicStatus = async (imovelId, status) => {
    const publicSnap = await get(databaseRef(db, `imoveisPublicos/${imovelId}`));
    return publicSnap.exists()
      ? { [`/imoveisPublicos/${imovelId}/imovelInfo/status`]: status }
      : {};
  };

  // Reconfere se o imóvel ainda existe (com imovelInfo completo) bem antes
  // de qualquer escrita em /imoveis/{id}. A lista vem de um onValue e pode
  // ficar desatualizada — se outro admin excluiu o registro enquanto esta
  // tela estava aberta, um update() em imovelInfo/status recriaria um nó
  // parcial (mesma classe de bug corrigida nas Functions de watermark, que
  // recriou o imóvel #200).
  const ensureImovelExists = async (imovelId) => {
    const snap = await get(databaseRef(db, `imoveis/${imovelId}/imovelInfo`));
    if (!snap.exists()) {
      showToast(
        "Este registro não existe mais ou está incompleto. Atualize a página antes de continuar.",
        "error"
      );
      return false;
    }
    return true;
  };

  const toggleStatus = async (imovelId, currentStatus) => {
    if (!(await ensureImovelExists(imovelId))) return;

    if (currentStatus === false) {
      const updates = {
        [`/imoveis/${imovelId}/imovelInfo/status`]: true,
        [`/imoveis/${imovelId}/imovelInfo/dataStatus`]: new Date().toISOString(),
        [`/imoveis/${imovelId}/imovelInfo/statusInfo`]: null,
        ...(await syncPublicStatus(imovelId, true)),
      };

      return update(databaseRef(db), updates).then(() => {
        showToast("Imóvel reativado com sucesso!", "success");
      });
    }

    setSelectedImovel(imovelId);
    setOpenModal(true);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 450,
    bgcolor: "background.paper",
    borderRadius: "10px",
    boxShadow: 24,
    p: 4,
  };

  const [openModal, setOpenModal] = useState(false);
  const [selectedImovel, setSelectedImovel] = useState(null);
  const [statusForm, setStatusForm] = useState({
    tipo: "",
    corretor: "",
    valorVenda: "",
    comissao: "",
    motivo: "",
  });

  const handleSaveStatusInfo = async () => {
    if (!selectedImovel) return;
    if (!(await ensureImovelExists(selectedImovel))) return;

    const valorNumerico = Number(
      statusForm.valorVenda.replace(/[R$\.\s]/g, "").replace(",", ".")
    );

    const updates = {
      [`/imoveis/${selectedImovel}/imovelInfo/status`]: false,
      [`/imoveis/${selectedImovel}/imovelInfo/dataStatus`]: new Date().toISOString(),
      [`/imoveis/${selectedImovel}/imovelInfo/statusInfo`]: {
        ...statusForm,
        valorVenda: valorNumerico,
      },
      // statusInfo (motivo/corretor/comissão) é informação interna e não é
      // replicado para /imoveisPublicos — só o status vai para o site.
      ...(await syncPublicStatus(selectedImovel, false)),
    };

    await update(databaseRef(db), updates);

    showToast("Status atualizado com sucesso!", "success");

    setOpenModal(false);
    setStatusForm({
      tipo: "",
      corretor: "",
      valorVenda: "",
      comissao: "",
      motivo: "",
      outroTexto: "",
    });


  };

  const formatCurrency = (value) => {
    if (!value) return "";
    const numeric = value.replace(/\D/g, "");
    const formatted = (Number(numeric) / 100).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
    return formatted;
  };


  return (
    <>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Motivo da desativação
          </Typography>

          {/* Seleção principal */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Selecione uma opção</InputLabel>
            <Select
              label="Selecione uma opção"
              value={statusForm.tipo}
              onChange={(e) =>
                setStatusForm({ ...statusForm, tipo: e.target.value })
              }
            >
              <MenuItem value="vendido_senna">
                Vendido/Alugado pela Senna Viannini
              </MenuItem>
              <MenuItem value="desativado">
                Apenas desativado do site
              </MenuItem>
            </Select>
          </FormControl>

          {/* Opção: vendido pela Senna */}
          {statusForm.tipo === "vendido_senna" && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Corretor responsável</InputLabel>
                <Select
                  label="Corretor responsável"
                  value={statusForm.corretor}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, corretor: e.target.value })
                  }
                >
                  {Object.entries(corretores).map(([id, info]) => (
                    <MenuItem key={id} value={id}>
                      {info.nome} {info.sobrenome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Valor da venda"
                fullWidth
                sx={{ mb: 2 }}
                value={statusForm.valorVenda}
                onChange={(e) => {
                  const formatted = formatCurrency(e.target.value);
                  setStatusForm({ ...statusForm, valorVenda: formatted });
                }}
              />

              <TextField
                label="Comissão (R$)"
                type="number"
                fullWidth
                sx={{ mb: 2 }}
                value={statusForm.comissao}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, comissao: e.target.value })
                }
              />
            </>
          )}

          {/* Opção: desativado com motivo */}
          {statusForm.tipo === "desativado" && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Motivo</InputLabel>
                <Select
                  label="Motivo"
                  value={statusForm.motivo}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, motivo: e.target.value })
                  }
                >
                  <MenuItem value="outra_imobiliaria">
                    Vendido/Alugado por outra imobiliária
                  </MenuItem>
                  <MenuItem value="desistencia">
                    Desistência do proprietário
                  </MenuItem>
                  <MenuItem value="outro">Outro motivo</MenuItem>
                </Select>
              </FormControl>

              {statusForm.motivo === "outro" && (
                <TextField
                  label="Descreva o motivo"
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                  value={statusForm.outroTexto}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, outroTexto: e.target.value })
                  }
                />
              )}
            </>
          )}

          {/* Botões */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button variant="outlined" color="error" onClick={() => setOpenModal(false)}>
              Cancelar
            </Button>

            <Button variant="contained" color="success" onClick={handleSaveStatusInfo}>
              Salvar
            </Button>
          </Box>
        </Box>
      </Modal>

      <div className="admin-page">
        <h1>Bem-vindo ao Painel Administrativo</h1>
        <p>Gerencie os imóveis cadastrados no sistema.</p>

        <div className="property-list">
          <div className="headerLista">
            <h2>Listagem de Imóveis</h2>
            <input
              placeholder="Pesquisar (Código, Bairro, Cidade, Proprietário)"
              type="text"
              className="inputSearch"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="imoveisLista">
            {filteredImoveis.length > 0 ? (
              filteredImoveis.slice(0, visibleCount).map((imovel) => (
                <div className="imovel" key={imovel.id}>
                  <img
                    src={
                      imovel.imagesLinks?.[0] || "https://via.placeholder.com/150"
                    }
                    alt="Imagem do Imóvel"
                  />

                  <div className="imovelInfos">
                    <h4>
                      Código do imóvel:{" "}
                      <span>{imovel?.imovelInfo?.codigo || "N/A"}</span>
                    </h4>
                    <h4>
                      Captador:{" "}
                      <span>
                        {corretores[imovel?.imovelInfo?.corretor]?.nome || "N/A"}{" "}
                        {corretores[imovel?.imovelInfo?.corretor]?.sobrenome ||
                          "N/A"}
                      </span>
                    </h4>
                    <h4>
                      Modalidade:{" "}
                      <span>{imovel?.imovelInfo?.modalidade || "N/A"}</span>
                    </h4>
                    <h4>
                      Tipo: <span>{imovel?.imovelInfo?.tipo || "N/A"}</span>
                    </h4>
                    <h4>
                      Endereço:{" "}
                      <span>{imovel?.imovelInfo?.endereco || "N/A"}</span>
                    </h4>
                    <h4>
                      Bairro: <span>{imovel?.imovelInfo?.bairro || "N/A"}</span>
                    </h4>
                    <h4>
                      Cidade: <span>{imovel?.imovelInfo?.cidade || "N/A"}</span>
                    </h4>
                    <h4>
                      Proprietário:{" "}
                      <span>
                        {imovel?.ownerInfo?.nome || imovel?.ownerInfo?.sobrenome
                          ? `${imovel?.ownerInfo?.nome || ""} ${imovel?.ownerInfo?.sobrenome || ""}`.trim()
                          : "Não informado"}
                      </span>
                    </h4>
                    <h4>
                      Telefone:{" "}
                      <span>
                        {imovel?.ownerInfo?.telefone || "N/A"}
                      </span>
                      <h4>
                        Valor:{" "}
                        <span>
                          {imovel?.informacoesComerciais?.valor || "N/A"}
                        </span>
                      </h4>
                    </h4>
                  </div>

                  <div className="imovelActions">
                    <div className="buttonsImovel">
                      <button onClick={() => handleNavigate(imovel?.id)}>
                        <FaPencilAlt />
                      </button>

                      <button
                        onClick={() =>
                          toggleStatus(imovel?.id, imovel?.imovelInfo?.status)
                        }
                        disabled={updatingStatus[imovel?.id]}
                        className={
                          imovel?.imovelInfo?.status ? "active" : "inactive"
                        }
                      >
                        <FaEye
                          style={{
                            color: imovel?.imovelInfo?.status
                              ? "#303926"
                              : "#c25353",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>Nenhum imóvel encontrado.</p>
            )}
          </div>

          {visibleCount < filteredImoveis.length && (
            <div style={{ textAlign: "center", margin: "20px 0" }}>
              <button onClick={handleLoadMore} className="btnCarregarMais">
                Carregar mais
              </button>
            </div>
          )}
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ListaImoveis;
